<script setup>
import { Head, Link, useForm } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import InputNumber from "primevue/inputnumber";
import Checkbox from "primevue/checkbox";
import Message from "primevue/message";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import { computed, ref, watch } from "vue";
import { useToast } from "primevue/usetoast";

const props = defineProps([
    'companies',
    'services',
    'flash',
]);

const errors = ref({});
const toast = useToast();
const filteredServices = ref([]);

const form = useForm({
    company_id: props.companies.length === 1 ? props.companies[0].id : null,
    name: '',
    description: '',
    sort_order: 0,
    is_active: true,
    services: [],
});

watch(() => form.company_id, (newId) => {
    if (newId && props.services) {
        filteredServices.value = props.services.filter(s => s.company_id === newId);
    } else {
        filteredServices.value = props.services || [];
    }
}, { immediate: true });

const submit = () => {
    form.post('/management/service-categories', {
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolgreich',
                detail: 'Dienstleistungskategorie wurde erfolgreich gespeichert.',
                life: 5000,
            });
        },
        onError: (err) => {
            errors.value = err;
            toast.add({
                severity: 'error',
                summary: 'Fehler beim Speichern',
                detail: 'Bitte prüfen Sie die Eingaben in den markierten Feldern.',
                life: 5000,
            });
        }
    });
};

const hasErrors = computed(() => Object.values(errors.value).some(v => v));

</script>

<template>
    <Layout :flash="flash">
        <Head title="Kategorie erstellen" />

        <div class="text-sm">
            <h1 class="text-2xl font-bold mb-8 text-gray-800">Neue Dienstleistungskategorie erstellen</h1>

            <Message v-if="hasErrors" severity="error" class="mb-6">
                <template v-for="(error, key) in errors" :key="key">
                    <div v-if="error">{{ Array.isArray(error) ? error[0] : error }}</div>
                </template>
            </Message>

            <form @submit.prevent="submit" class="space-y-8">
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        <div v-if="$page.props.auth.user.role === 'super_admin'" class="flex flex-col gap-2">
                            <label for="company_id" class="font-bold text-gray-600">Unternehmen *</label>
                            <Dropdown id="company_id" v-model="form.company_id" :options="companies" optionLabel="name" optionValue="id" :class="{'p-invalid': errors.company_id}" class="w-full" placeholder="Unternehmen wählen" @change="errors.company_id = null" />
                            <small v-if="errors.company_id" class="p-error">{{ errors.company_id }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="name" class="font-bold text-gray-600">Name der Kategorie *</label>
                            <InputText id="name" v-model="form.name" :class="{'p-invalid': errors.name}" class="w-full" placeholder="z.B. Massage" @input="errors.name = null" />
                            <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="sort_order" class="font-bold text-gray-600">Sortierung</label>
                            <InputNumber id="sort_order" v-model="form.sort_order" class="w-full" showButtons :min="0" />
                        </div>

                        <div class="flex flex-col gap-2 justify-end">
                            <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                                <Checkbox id="is_active" v-model="form.is_active" :binary="true" />
                                <label for="is_active" class="font-bold text-gray-700 cursor-pointer">Kategorie aktiv</label>
                            </div>
                        </div>

                        <div class="flex flex-col gap-2 md:col-span-2">
                            <label for="services" class="font-bold text-gray-600">Zugeordnete Dienstleistungen</label>
                            <MultiSelect id="services" v-model="form.services" :options="filteredServices" optionLabel="name" optionValue="id" 
                                         placeholder="Dienstleistungen auswählen" filter class="w-full" :disabled="!form.company_id && $page.props.auth.user.role === 'super_admin'" />
                            <p v-if="!form.company_id && $page.props.auth.user.role === 'super_admin'" class="text-xs text-gray-400">Bitte wählen Sie zuerst ein Unternehmen aus.</p>
                        </div>

                        <div class="flex flex-col gap-2 md:col-span-2">
                            <label for="description" class="font-bold text-gray-600">Beschreibung</label>
                            <Textarea id="description" v-model="form.description" rows="4" class="w-full" placeholder="Zusätzliche Informationen..." />
                        </div>
                    </div>
                </div>

                <div class="flex justify-between items-center pt-8 border-t mt-10">
                    <Button asChild v-slot="slotProps" severity="secondary" outlined>
                        <Link href="/management/service-categories" :class="slotProps.class" class="px-8 py-3 rounded-xl border-gray-300 font-bold">
                            <i class="pi pi-arrow-left mr-2" />
                            Abbrechen
                        </Link>
                    </Button>

                    <Button type="submit" 
                            class="!bg-black !text-[#FFF053] !border-none px-10 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-gray-200" 
                            :loading="form.processing" :disabled="form.processing">
                        <i class="pi pi-save" />
                        Kategorie speichern
                    </Button>
                </div>
            </form>
        </div>
    </Layout>
</template>
